import * as React from 'react';
import { css, StyleSheet } from 'aphrodite/no-important';
import { useTheme } from '../../../theming/useTheme';
import { MEDIA_BREAKPOINTS } from '../../../mediaQueries.const';
import { baseUnit } from '../../../theming/baseUnitDefinition';
import { CardComponentDataProps } from '../../partials/cards/cards.utils';
import {
  componentConfigThemeKey as onTopCardConfigThemeKey,
  OnTopCard,
  OnTopCardComponentThemeProps,
} from '../../partials/cards/onTopCard/OnTopCard';
import { createImageWidths } from '../../partials/image/image.utils';
import { useThemeOverride } from '../../../theming/useThemeOverride';
import { ThemeContext } from '../../../theming/ThemeProviderWithFonts';
import { ErrorBoundary } from '../../errorHandling/ErrorBoundary';

export const componentConfigThemeKey = 'oneThreeOTC';

export interface OneThreeOTCThemeProps {
  largeVerticalGap: number;
  largeHorizontalGap: number;
  mediumVerticalGap: number;
  mediumHorizontalGap: number;
  smallHorizontalGap: number;
}

interface OneThreeOTCDataProps {
  cards: Array<CardComponentDataProps>;
}

const imageWidthsFirstCard = createImageWidths(360, 720, 1080);
const imageWidthsSecondAndThirdCards = createImageWidths(360, 360, 360);
const imageWidthsFourthCard = createImageWidths(360, 720, 360);

const getStyle = (theme: OneThreeOTCThemeProps) => {
  const { largeHorizontalGap, largeVerticalGap, mediumHorizontalGap, mediumVerticalGap, smallHorizontalGap } = theme;
  const firstCardArea = 'first-card';
  const secondCardArea = 'second-card';
  const thirdCardArea = 'third-card';
  const fourthCardArea = 'fourth-card';
  return StyleSheet.create({
    wrapper: {
      marginTop: `${baseUnit * 0.25}px`,
      [MEDIA_BREAKPOINTS.large]: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 1fr',
        gridTemplateRows: 'auto',
        gridColumnGap: `${largeVerticalGap * baseUnit}px`,
        gridRowGap: `${largeHorizontalGap * baseUnit}px`,
        gridTemplateAreas: `'${firstCardArea} ${firstCardArea} ${firstCardArea}' '${secondCardArea} ${thirdCardArea} ${fourthCardArea}'`,
      },
      [MEDIA_BREAKPOINTS.medium]: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gridTemplateRows: 'auto',
        gridTemplateAreas: `'${firstCardArea} ${firstCardArea}' '${secondCardArea} ${thirdCardArea}' '${fourthCardArea} ${fourthCardArea}'`,
        gridColumnGap: `${mediumVerticalGap * baseUnit}px`,
        gridRowGap: `${mediumHorizontalGap * baseUnit}px`,
      },
      [MEDIA_BREAKPOINTS.small]: {
        display: 'grid',
        gridTemplateColumns: '1fr',
        gridTemplateRows: 'auto',
        gridTemplateAreas: `'${firstCardArea}' '${secondCardArea}' '${thirdCardArea}' '${fourthCardArea}'`,
        gridRowGap: `${smallHorizontalGap * baseUnit}px`,
      },
    },
    firstCard: {
      gridArea: firstCardArea,
    },
    secondCard: {
      gridArea: secondCardArea,
    },
    thirdCard: {
      gridArea: thirdCardArea,
    },
    fourthCard: {
      gridArea: fourthCardArea,
    },
  });
};
export const OneThreeOTC: React.FunctionComponent<OneThreeOTCDataProps> = props => {
  const { cards } = props;
  const themeProps = useTheme<OneThreeOTCThemeProps>(componentConfigThemeKey);
  const style = getStyle(themeProps);
  const themeContextFirstCard = useThemeOverride<OnTopCardComponentThemeProps>({
    titleFontSizeMedium: 'huge',
    titleFontSizeLarge: 'huge',
    titleFontSizeSmall: 'bigger',
    horizontalGapSmall: 0.25,
    horizontalGapMedium: 0.5,
    horizontalGapLarge: 0.5,
    aspectRatioSmall: { x: 4, y: 3 },
  }, onTopCardConfigThemeKey);
  const themeContextSecondAndThirdCards = useThemeOverride<OnTopCardComponentThemeProps>({
    titleFontSizeMedium: 'bigger',
    titleFontSizeLarge: 'bigger',
    titleFontSizeSmall: 'bigger',
    horizontalGapSmall: 0.25,
    horizontalGapMedium: 0.25,
    horizontalGapLarge: 0.25,
  }, onTopCardConfigThemeKey);
  const themeContextFourthCards = useThemeOverride<OnTopCardComponentThemeProps>({
    titleFontSizeMedium: 'huge',
    titleFontSizeLarge: 'bigger',
    titleFontSizeSmall: 'bigger',
    horizontalGapSmall: 0.25,
    horizontalGapMedium: 0.25,
    horizontalGapLarge: 0.25,
  }, onTopCardConfigThemeKey);
  return (
    <ErrorBoundary>
      {cards.length > 0
      && (
      <div className={css(style.wrapper)}>
        <ThemeContext.Provider value={themeContextFirstCard}>
          <div className={css(style.firstCard)}>
            <OnTopCard {...cards[0]} imageWidths={imageWidthsFirstCard} />
          </div>
        </ThemeContext.Provider>
        <ThemeContext.Provider value={themeContextSecondAndThirdCards}>
          <div className={css(style.secondCard)}>
            <OnTopCard {...cards[1]} imageWidths={imageWidthsSecondAndThirdCards} />
          </div>
          <div className={css(style.thirdCard)}>
            <OnTopCard {...cards[2]} imageWidths={imageWidthsSecondAndThirdCards} />
          </div>
        </ThemeContext.Provider>
        <ThemeContext.Provider value={themeContextFourthCards}>
          <div className={css(style.fourthCard)}>
            <OnTopCard {...cards[3]} imageWidths={imageWidthsFourthCard} />
          </div>
        </ThemeContext.Provider>
      </div>
      )}
    </ErrorBoundary>
  );
};
